import { getOpenCustomQuantityRange } from '@/lib/product-options/open-custom-quantity';
import { getClosedCustomQuantities } from '@/lib/product-options/closed-custom-quantity';

/**
 * Aantalbeleid dat met een winkelwagenregel meereist.
 *
 * De productpagina normaliseert het aantal naar de staffel van het product,
 * maar de winkelwagen-stepper deed +1/-1 en stuurde zo `copies=1` of
 * `copies=2` naar /api/pricing/calculate voor producten die pas vanaf 10 of
 * 25 stuks bestaan (Vercel runtime errors "Ongeldige optie: copies=1",
 * september-oktober 2026). Het beleid wordt bij het toevoegen aan de
 * winkelwagen vastgelegd, zodat de winkelwagen het product niet opnieuw hoeft
 * op te halen. Oudere regels zonder beleid houden het gedrag per stuk.
 *
 * `step: 0` betekent: alleen de vaste staffels (presets) zijn bestelbaar.
 */
export type CartQuantityPolicy = {
  minimum: number;
  maximum: number;
  step: number;
  presets: number[];
};

type OptionLike = { id?: string; name?: string; type?: string; values?: Array<{ value?: unknown; priceModifier?: unknown }> };

export function cartQuantityPolicyFromProduct(
  providerType: string | undefined,
  options: OptionLike[] | undefined | null,
  specifications: Record<string, any> | undefined | null,
): CartQuantityPolicy | null {
  if (providerType !== 'CUSTOM') return null;
  const copiesOption = (options || []).find((option) => option?.id === 'copies' || option?.name === 'copies');
  if (!copiesOption || !Array.isArray(copiesOption.values) || copiesOption.values.length === 0) return null;

  const open = getOpenCustomQuantityRange(options, specifications);
  if (open) {
    return { minimum: open.minimum, maximum: open.maximum, step: open.step, presets: open.presets };
  }

  const closed = getClosedCustomQuantities(providerType, specifications)
    ?? [...new Set(copiesOption.values.map((value) => Number(value?.value)).filter((value) => Number.isSafeInteger(value) && value > 0))]
      .sort((a, b) => a - b);
  if (closed.length === 0) return null;
  return { minimum: closed[0], maximum: closed[closed.length - 1], step: 0, presets: closed };
}

function nearestPreset(quantity: number, presets: number[]) {
  return presets.reduce((best, preset) => (Math.abs(preset - quantity) < Math.abs(best - quantity) ? preset : best), presets[0]);
}

/** Het aantal dat de stepper na een klik op + of - moet aanvragen. */
export function stepCartQuantity(
  current: number,
  direction: 1 | -1,
  policy: CartQuantityPolicy | null | undefined,
): number {
  const quantity = Number.isFinite(current) && current > 0 ? Math.floor(current) : 1;
  if (!policy) return Math.max(1, quantity + direction);

  const presets = [...new Set(policy.presets)].filter((value) => Number.isSafeInteger(value) && value > 0).sort((a, b) => a - b);
  if (policy.step <= 0) {
    if (presets.length === 0) return Math.max(1, quantity + direction);
    const index = presets.indexOf(quantity);
    if (index >= 0) return presets[Math.min(presets.length - 1, Math.max(0, index + direction))];
    // Een aantal buiten de staffel (oude regel, handmatig aangepast): eerst
    // naar de dichtstbijzijnde staffel in de gevraagde richting.
    const higher = presets.find((preset) => preset > quantity);
    const lower = [...presets].reverse().find((preset) => preset < quantity);
    if (direction > 0) return higher ?? presets[presets.length - 1];
    return lower ?? presets[0];
  }

  const minimum = Math.max(1, policy.minimum);
  const maximum = Math.max(minimum, policy.maximum);
  if (quantity < minimum) return minimum;
  if (quantity > maximum) return maximum;
  const aligned = presets.includes(quantity) || (quantity - minimum) % policy.step === 0
    ? quantity
    : nearestPreset(quantity, presets.length ? presets : [minimum]);
  const next = aligned === quantity ? quantity + direction * policy.step : aligned;
  return Math.min(maximum, Math.max(minimum, next));
}
